import React, { useEffect, useState } from 'react';
import HeroCarousel from '../components/HeroCarousel';
import CategoriesRow from '../components/CategoriesRow';
import ProductCard from '../components/ProductCard';
import Testimonials from '../components/Testimonials';
import CoverageModal from '../components/CoverageModal';
import api from '../lib/api';
import { Link } from 'react-router-dom';
import { ArrowRight, Flame, Sparkles, Zap, Truck, Shield, Award, CreditCard, MapPin, ShoppingCart, Bell, Package, CheckCircle, Clock, Gift, Star } from 'lucide-react';

const DEFAULT_SECTIONS = [
  { id: 'hero', enabled: true, title: 'Hero (carrusel)', subtitle: null },
  { id: 'categories', enabled: true, title: 'Categorías', subtitle: 'Encuentra lo que buscas' },
  { id: 'benefits', enabled: true, title: 'Beneficios', subtitle: null },
  { id: 'vhill_top', enabled: true, title: 'Los más vendidos', subtitle: 'Nuestras estrellas VHILL en 3K, 12K y 32K' },
  { id: 'featured', enabled: true, title: 'Novedades', subtitle: 'Los productos más buscados de la semana' },
  { id: 'desechables', enabled: true, title: 'Vapes Desechables', subtitle: 'Listos para vapear, máxima autonomía' },
  { id: 'ejuice', enabled: true, title: 'E-Juice', subtitle: 'Líquidos premium para tu vape' },
  { id: 'brands', enabled: true, title: 'Marcas reconocidas', subtitle: 'Solo lo más original' },
  { id: 'kits', enabled: true, title: 'Kits y Mods', subtitle: 'Para el vapeador más exigente' },
  { id: 'how_to_order', enabled: true, title: '¿Cómo hago un pedido?', subtitle: 'Simple, rápido y seguro. Sigue estos 5 pasos y recibe tu pedido el mismo día.' },
  { id: 'testimonials', enabled: true, title: 'Testimonios', subtitle: null },
];

const Section = ({ title, subtitle, action, children }) => (
  <section className="max-w-[1400px] mx-auto px-4 md:px-6 mt-14">
    <div className="flex items-end justify-between mb-5">
      <div>
        <h3 className="text-2xl md:text-3xl font-black text-white">{title}</h3>
        {subtitle && <p className="text-sm text-white/50 mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const ICON_MAP = {
  flame: Flame,
  zap: Zap,
  sparkles: Sparkles,
  truck: Truck,
  shield: Shield,
  award: Award,
  'credit-card': CreditCard,
};

const BenefitsStrip = ({ sec }) => {
  const cards = sec?.cards || [
    { icon: 'flame', title: 'Vapes Desechables', desc: 'Sabor listo para usar', link: '/catalogo?cat=Vape%20Desechable' },
    { icon: 'zap', title: 'Kits y Mods', desc: 'Máxima potencia', link: '/catalogo?cat=Kits' },
    { icon: 'sparkles', title: 'E-Juice Premium', desc: 'Sabores originales', link: '/catalogo?cat=e-juice' }
  ];

  return (
    <section className="max-w-[1400px] mx-auto px-4 md:px-6 mt-12">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((b, idx) => {
          const Ic = ICON_MAP[b.icon] || Flame;
          const content = (
            <div className="flex items-center gap-4 bg-gradient-to-b from-neutral-950 to-black rounded-2xl border border-white/5 p-6 hover:border-orange-500/30 hover:shadow-[0_8px_30px_rgba(249,115,22,0.05)] transition-all duration-300 group cursor-pointer h-full">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 text-orange-500 flex items-center justify-center group-hover:scale-110 group-hover:bg-orange-500/20 transition-all duration-300 shrink-0">
                <Ic className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-white text-base group-hover:text-orange-400 transition-colors flex items-center gap-1.5">
                  {b.title}
                  {b.link && <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-orange-400" />}
                </p>
                <p className="text-sm text-white/50 mt-1 leading-relaxed">{b.desc}</p>
              </div>
            </div>
          );

          if (b.link) {
            return (
              <Link key={idx} to={b.link} className="no-underline">
                {content}
              </Link>
            );
          }
          return <div key={idx}>{content}</div>;
        })}
      </div>
    </section>
  );
};

/* ─── Cobertura step is fixed (not editable from admin) ─── */
const FIXED_COBERTURA = {
  title: 'Cobertura',
  desc: '¿Llegamos a tu domicilio? Verifica tu zona antes de hacer tu pedido para asegurarte de que podemos entregarte.',
  icon: 'map-pin',
};

const DEFAULT_HOW_TO_ORDER = [
  { title: 'Realiza tu Pedido', desc: 'Explora el catálogo, elige tus productos y agrégalos al carrito. Selecciona tu método de pago y confirma tu orden.', icon: 'shopping-cart' },
  { title: 'Pago', desc: 'Paga en efectivo al momento de la entrega o vía transferencia bancaria. Si pagas por transferencia, sube el screenshot de tu comprobante antes de confirmar el pedido.', icon: 'credit-card' },
  { title: 'Confirmación', desc: 'Una vez confirmado tu pago, recibirás un correo y una notificación en la app con el estado de tu pedido: En proceso, En camino y Entregado.', icon: 'bell' },
  { title: 'Recibe tu Pedido', desc: 'Entregamos el mismo día. El tiempo estimado es de 20 minutos a 3 horas según tu ubicación dentro de nuestra zona de cobertura.', icon: 'truck' },
];

const STEP_ICON_MAP = {
  'shopping-cart': ShoppingCart,
  'credit-card': CreditCard,
  'bell': Bell,
  'truck': Truck,
  'package': Package,
  'check-circle': CheckCircle,
  'zap': Zap,
  'shield': Shield,
  'award': Award,
  'clock': Clock,
  'map-pin': MapPin,
  'gift': Gift,
  'star': Star,
};

const HowToOrder = ({ sec, steps, onVerify }) => {
  const editableSteps = Array.isArray(steps) && steps.length > 0 ? steps : DEFAULT_HOW_TO_ORDER;
  const allCards = [FIXED_COBERTURA, ...editableSteps];

  return (
    <section
      className="mt-16 py-20 border-y border-white/5 relative overflow-hidden"
      style={{ background: '#000' }}
    >
      {/* Subtle center glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(249,115,22,0.06) 0%, transparent 70%)' }}
      />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 relative">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="h-px w-8 bg-orange-500/50" />
            <span className="text-[11px] font-black uppercase tracking-[0.3em]" style={{ color: '#f97316' }}>
              Proceso de Compra
            </span>
            <span className="h-px w-8 bg-orange-500/50" />
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight">
            {sec?.title || '¿Cómo hago un pedido?'}
          </h2>
          {sec?.subtitle && (
            <p className="mt-4 text-white text-sm sm:text-base max-w-lg mx-auto leading-relaxed">
              {sec.subtitle}
            </p>
          )}
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {allCards.map((step, idx) => {
            const IconComp = (step.icon && STEP_ICON_MAP[step.icon]) || ShoppingCart;
            return (
              <div
                key={idx}
                data-testid={`how-to-order-step-${idx}`}
                className="relative rounded-2xl p-6 flex flex-col gap-3 transition-all duration-300 group"
                style={{
                  background: 'linear-gradient(145deg, #161616 0%, #0e0e0e 100%)',
                  border: '1px solid rgba(249,115,22,0.18)',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.border = '1px solid rgba(249,115,22,0.55)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(249,115,22,0.14), 0 2px 12px rgba(0,0,0,0.4)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.border = '1px solid rgba(249,115,22,0.18)';
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.4)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Top accent line */}
                <div
                  className="absolute top-0 left-6 right-6 h-[2px] rounded-b-full opacity-0 transition-opacity duration-300"
                  style={{ background: 'linear-gradient(90deg, transparent, #f97316, transparent)' }}
                  ref={(el) => {
                    if (el) {
                      el.parentElement.addEventListener('mouseenter', () => { el.style.opacity = '1'; });
                      el.parentElement.addEventListener('mouseleave', () => { el.style.opacity = '0'; });
                    }
                  }}
                />

                {/* Step number watermark */}
                <span
                  className="absolute top-4 right-5 text-6xl font-black leading-none select-none pointer-events-none"
                  style={{ color: 'rgba(249,115,22,0.1)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {String(idx + 1).padStart(2, '0')}
                </span>

                {/* Icon */}
                <div className="flex justify-center pt-2 mb-2">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 transition-transform duration-300"
                    style={{
                      background: 'linear-gradient(135deg, #f97316 0%, #ea6a0a 100%)',
                      boxShadow: '0 4px 16px rgba(249,115,22,0.3)',
                      outline: '2px solid rgba(249,115,22,0.25)',
                      outlineOffset: '3px',
                    }}
                  >
                    <IconComp className="w-7 h-7 text-black" strokeWidth={2.2} />
                  </div>
                </div>

                {/* Title */}
                <p className="font-black text-white text-base leading-snug text-center tracking-tight">
                  {step.title}
                </p>

                {/* Description */}
                <p className="text-white text-sm leading-relaxed flex-1 text-center">
                  {step.desc}
                </p>

                {/* Coverage button */}
                {idx === 0 && (
                  <button
                    onClick={onVerify}
                    data-testid="how-to-order-verify-coverage"
                    className="mt-3 w-full h-10 rounded-full text-xs uppercase flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.04] hover:shadow-[0_0_20px_rgba(249,115,22,0.6)] active:scale-95"
                    style={{
                      background: 'linear-gradient(135deg, #f97316 0%, #ea6a0a 100%)',
                      color: '#000',
                      fontWeight: 900,
                      letterSpacing: '0.08em',
                      boxShadow: '0 4px 14px rgba(249,115,22,0.35)',
                    }}
                  >
                    <MapPin className="w-4 h-4" style={{ color: '#000', strokeWidth: 2.5 }} />
                    Verificar Cobertura
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ProductGrid = ({ items, limit = 10 }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
    {items.slice(0, limit).map((p) => <ProductCard key={p.product_id} product={p} />)}
  </div>
);

const DEFAULT_HOME_SUBCATS = [
  { id: 'vhill_3k',  label: 'VHILL 3K',  category: 'VHILL 3K',  color: '#f97316', visible: true, order: 1 },
  { id: 'vhill_12k', label: 'VHILL 12K', category: 'VHILL 12K', color: '#f97316', visible: true, order: 2 },
  { id: 'vhill_32k', label: 'VHILL 32K', category: 'VHILL 32K', color: '#f97316', visible: true, order: 3 },
];

const HomePage = () => {
  const [sections, setSections] = useState(null); // null = loading, render nothing until API resolves
  const [featured, setFeatured] = useState(null);
  const [desechables, setDesechables] = useState(null);
  const [ejuice, setEjuice] = useState(null);
  const [kits, setKits] = useState(null);
  const [brands, setBrands] = useState([]);
  const [subcats, setSubcats] = useState(DEFAULT_HOME_SUBCATS);
  const [subcatProducts, setSubcatProducts] = useState({});
  const [subcatsReady, setSubcatsReady] = useState(false);
  const [customProducts, setCustomProducts] = useState({});
  const [howToOrderSteps, setHowToOrderSteps] = useState(null);
  const [showCoverage, setShowCoverage] = useState(false);

  useEffect(() => {
    api.get('/settings').then((r) => {
      const arr = r.data?.home_sections;
      // Merge stored sections with DEFAULT_SECTIONS:
      // - Preserve stored order/settings for existing sections
      // - Append new predefined sections that aren't stored yet
      if (Array.isArray(arr) && arr.length) {
        const storedIds = new Set(arr.map((s) => s.id));
        // Only auto-append how_to_order if missing (it was added after initial deploy)
        // Do NOT re-add other sections — they may have been intentionally deleted by admin
        const toAppend = DEFAULT_SECTIONS.filter((s) => !storedIds.has(s.id) && s.id === 'how_to_order');
        const finalSections = [...arr, ...toAppend];
        setSections(finalSections);
        const predefinedIds = ['hero', 'categories', 'benefits', 'vhill_top', 'featured', 'desechables', 'ejuice', 'brands', 'kits', 'testimonials', 'how_to_order'];
        finalSections.forEach((sec) => {
          if (sec.enabled !== false && (sec.type === 'category' || sec.id.startsWith('custom_') || !predefinedIds.includes(sec.id))) {
            if (sec.category) {
              api.get(`/products?category=${encodeURIComponent(sec.category)}&limit=12`)
                .then((res) => setCustomProducts((prev) => ({ ...prev, [sec.id]: res.data.items || [] })))
                .catch(() => {});
            }
          }
        });
      } else {
        setSections(DEFAULT_SECTIONS);
      }

      const subs = r.data?.home_subcategories;
      const finalSubs = Array.isArray(subs) && subs.length ? subs : DEFAULT_HOME_SUBCATS;
      const visibleSorted = finalSubs
        .filter((s) => s.visible !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSubcats(visibleSorted);
      if (Array.isArray(r.data?.how_to_order_steps) && r.data.how_to_order_steps.length > 0)
        setHowToOrderSteps(r.data.how_to_order_steps);
      // Fetch popular products for each visible subcategory
      const subFetches = visibleSorted.map((sub) =>
        api.get(`/products/popular?category=${encodeURIComponent(sub.category)}&limit=12`)
          .then((res) => setSubcatProducts((prev) => ({ ...prev, [sub.id]: res.data.items || [] })))
          .catch(() => setSubcatProducts((prev) => ({ ...prev, [sub.id]: [] })))
      );
      Promise.allSettled(subFetches).then(() => setSubcatsReady(true));
    }).catch(() => {});

    api.get('/products?limit=12').then((r) => setFeatured(r.data.items || [])).catch(() => setFeatured([]));
    api.get('/products?category=Vape%20Desechable&limit=12').then((r) => setDesechables(r.data.items || [])).catch(() => setDesechables([]));
    api.get('/products?category=e-juice&limit=12').then((r) => setEjuice(r.data.items || [])).catch(() => setEjuice([]));
    api.get('/products?category=Kits&limit=12').then((r) => setKits(r.data.items || [])).catch(() => setKits([]));
    api.get('/vendors').then((r) => setBrands(r.data.slice(0, 10)));
  }, []);

  const renderSection = (s) => {
    const predefinedIds = ['hero', 'categories', 'benefits', 'vhill_top', 'featured', 'desechables', 'ejuice', 'brands', 'kits', 'testimonials', 'how_to_order'];
    const isCustom = s.type === 'category' || s.id.startsWith('custom_') || !predefinedIds.includes(s.id);
    if (isCustom) {
      const items = customProducts[s.id]; // undefined = loading, [] = empty
      if (!items || !items.length) return null;
      return (
        <Section
          key={s.id}
          title={s.title || 'Sección'}
          subtitle={s.subtitle}
          action={<Link to={`/catalogo?cat=${encodeURIComponent(s.category || '')}`} className="text-orange-400 font-semibold hover:text-orange-300 text-sm inline-flex items-center gap-1">Ver todo <ArrowRight className="w-4 h-4" /></Link>}
        >
          <ProductGrid items={items} limit={s.limit || 10} />
        </Section>
      );
    }

    switch (s.id) {
      case 'hero':
        return <HeroCarousel key="hero" />;
      case 'categories':
        return <CategoriesRow key="categories" title={s.title} subtitle={s.subtitle} />;
      case 'benefits':
        return <BenefitsStrip key="benefits" sec={s} />;
      case 'vhill_top': {
        if (!subcatsReady) return null;
        const total = subcats.reduce((n, sub) => n + ((subcatProducts[sub.id] || []).length), 0);
        if (!total) return null;
        return (
          <section key="vhill_top" className="max-w-[1400px] mx-auto px-4 md:px-6 mt-14" data-testid="home-vhill-top">
            <div className="flex items-end justify-between mb-6">
              <div>
                <h3 className="text-2xl md:text-3xl font-black text-white">{s.title}</h3>
                {s.subtitle && <p className="text-sm text-white/50 mt-1">{s.subtitle}</p>}
              </div>
              <Link to="/catalogo?vendor=VHILL" className="text-orange-400 font-semibold hover:text-orange-300 text-sm inline-flex items-center gap-1">Ver todo VHILL <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="space-y-8">
              {subcats.map((sub) => {
                const items = subcatProducts[sub.id] || [];
                if (!items.length) return null;
                const accent = sub.color || '#f97316';
                return (
                  <div key={sub.id} data-testid={`home-subcat-${sub.id}`}>
                    <div className="flex items-baseline justify-between mb-3">
                      <h4 className="text-lg md:text-xl font-black text-white flex items-center gap-2">
                        <span className="inline-block w-2 h-6 rounded-sm" style={{ backgroundColor: accent }}></span>
                        {sub.label}
                      </h4>
                      <Link
                        to={`/catalogo?cat=${encodeURIComponent(sub.category)}`}
                        className="text-xs font-semibold inline-flex items-center gap-1 hover:opacity-80"
                        style={{ color: accent }}
                      >
                        Ver {sub.label} <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                      {items.slice(0, sub.limit || 4).map((p) => <ProductCard key={p.product_id} product={p} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      }
      case 'featured':
        if (!featured || !featured.length) return null;
        return (
          <Section
            key="featured"
            title={s.title}
            subtitle={s.subtitle}
            action={<Link to="/catalogo" className="text-orange-400 font-semibold hover:text-orange-300 text-sm inline-flex items-center gap-1">Ver todo <ArrowRight className="w-4 h-4" /></Link>}
          >
            <ProductGrid items={featured} limit={s.limit || 10} />
          </Section>
        );
      case 'desechables':
        if (!desechables || !desechables.length) return null;
        return (
          <Section
            key="desechables"
            title={s.title}
            subtitle={s.subtitle}
            action={<Link to="/catalogo?cat=Vape%20Desechable" className="text-orange-400 font-semibold hover:text-orange-300 text-sm inline-flex items-center gap-1">Ver todos <ArrowRight className="w-4 h-4" /></Link>}
          >
            <ProductGrid items={desechables} limit={s.limit || 10} />
          </Section>
        );
      case 'ejuice':
        if (!ejuice || !ejuice.length) return null;
        return (
          <Section
            key="ejuice"
            title={s.title}
            subtitle={s.subtitle}
            action={<Link to="/catalogo?cat=e-juice" className="text-orange-400 font-semibold hover:text-orange-300 text-sm inline-flex items-center gap-1">Ver todos <ArrowRight className="w-4 h-4" /></Link>}
          >
            <ProductGrid items={ejuice} limit={s.limit || 10} />
          </Section>
        );
      case 'brands':
        if (!brands.length) return null;
        return (
          <Section key="brands" title={s.title} subtitle={s.subtitle}>
            <div className="flex gap-4 flex-wrap">
              {brands.map((b) => (
                <Link key={b.name} to={`/catalogo?vendor=${encodeURIComponent(b.name)}`} className="px-5 h-11 rounded-full bg-neutral-950 border border-white/10 hover:border-orange-500/50 hover:bg-orange-500/10 text-white font-semibold text-sm flex items-center gap-2 transition">
                  {b.name} <span className="text-white/40 text-xs">{b.count}</span>
                </Link>
              ))}
            </div>
          </Section>
        );
      case 'kits':
        if (!kits || !kits.length) return null;
        return (
          <Section
            key="kits"
            title={s.title}
            subtitle={s.subtitle}
            action={<Link to="/catalogo?cat=Kits" className="text-orange-400 font-semibold hover:text-orange-300 text-sm inline-flex items-center gap-1">Ver todos <ArrowRight className="w-4 h-4" /></Link>}
          >
            <ProductGrid items={kits} limit={s.limit || 10} />
          </Section>
        );
      case 'how_to_order':
        return <HowToOrder key="how_to_order" sec={s} steps={howToOrderSteps} onVerify={() => setShowCoverage(true)} />;
      case 'testimonials':
        return <Testimonials key="testimonials" />;
      default:
        return null;
    }
  };

  return (
    <div className="pb-32">
      {sections && sections.filter((s) => s.enabled !== false).map(renderSection)}
      <CoverageModal open={showCoverage} onOpenChange={(v) => setShowCoverage(v)} />
    </div>
  );
};

export default HomePage;
